import { cloneElement, isValidElement } from "react";
import Link from "next/link";

import {
  getUserAvatar,
  getUserDisplayName,
  getUserHandle,
  getUserId,
  getUserInitials,
  getUserProfileHref,
} from "../../utils/userIdentity";
import UserAvatar from "./UserAvatar";

const normalizeRenderableList = (value, prefix) =>
  (Array.isArray(value) ? value : [value])
    .filter(Boolean)
    .map((item, index) =>
      isValidElement(item) && item.key === null
        ? cloneElement(item, { key: `${prefix}-${index}` })
        : item,
    );

const UserIdentityCard = ({
  actions,
  avatar,
  badges,
  className = "",
  disabled = false,
  identity,
  initials,
  onSelect,
  profileHref,
  selected = false,
  showAvatar = true,
  size = 36,
  subtitle,
  title,
  user,
  variant = "row",
}) => {
  const resolvedTitle = title || getUserDisplayName(user);
  const resolvedSubtitle = subtitle === undefined
    ? user?.accountState?.status === "deactivated"
      ? "Account deactivated"
      : getUserHandle(user)
    : subtitle;
  const resolvedIdentity = identity || getUserId(user) || resolvedTitle || resolvedSubtitle;
  const resolvedAvatar = avatar === undefined ? getUserAvatar(user) : avatar;
  const resolvedAriaName =
    typeof resolvedTitle === "string"
      ? resolvedTitle
      : getUserDisplayName(user);
  const resolvedInitials = initials || getUserInitials(resolvedAriaName);
  const resolvedProfileHref =
    profileHref === undefined ? getUserProfileHref(user) : profileHref;
  const badgeItems = normalizeRenderableList(badges, "badge");
  const actionItems = normalizeRenderableList(actions, "action");
  const rootClassName = [
    "userIdentityCard",
    `userIdentityCard--${variant}`,
    onSelect ? "userIdentityCard--selectable" : "",
    selected ? "userIdentityCard--selected" : "",
    disabled ? "userIdentityCard--disabled" : "",
    !showAvatar ? "userIdentityCard--withoutAvatar" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");
  const stopRowSelection = (event) => event.stopPropagation();
  const selectFromKeyboard = (event) => {
    if (!onSelect || disabled || !["Enter", " "].includes(event.key)) return;
    event.preventDefault();
    onSelect(event);
  };
  const avatarNode = (
    <UserAvatar
      className="userIdentityCard__avatar"
      identity={resolvedIdentity}
      size={size}
      src={resolvedAvatar}
    >
      {resolvedInitials}
    </UserAvatar>
  );

  return (
    <div
      aria-disabled={onSelect && disabled ? "true" : undefined}
      aria-pressed={onSelect ? selected : undefined}
      className={rootClassName}
      onClick={onSelect && !disabled ? onSelect : undefined}
      onKeyDown={selectFromKeyboard}
      role={onSelect ? "button" : undefined}
      tabIndex={onSelect && !disabled ? 0 : undefined}
    >
      {showAvatar ? (resolvedProfileHref ? (
        <Link
          aria-label={`Open ${resolvedAriaName}'s profile`}
          className="userIdentityCard__avatarLink"
          href={resolvedProfileHref}
          onClick={stopRowSelection}
          onKeyDown={stopRowSelection}
        >
          {avatarNode}
        </Link>
      ) : (
        avatarNode
      )) : null}
      <div className="userIdentityCard__content">
        {resolvedProfileHref ? (
          <Link
            className="userIdentityCard__title"
            href={resolvedProfileHref}
            onClick={stopRowSelection}
            onKeyDown={stopRowSelection}
          >
            {resolvedTitle}
          </Link>
        ) : (
          <strong className="userIdentityCard__title">{resolvedTitle}</strong>
        )}
        {resolvedSubtitle && (
          <div className="userIdentityCard__subtitle">{resolvedSubtitle}</div>
        )}
      </div>
      {badgeItems.length > 0 && (
        <div
          className="userIdentityCard__badges"
          onClick={stopRowSelection}
          onKeyDown={stopRowSelection}
        >
          {badgeItems}
        </div>
      )}
      {actionItems.length > 0 && (
        <div
          className="userIdentityCard__actions"
          onClick={stopRowSelection}
          onKeyDown={stopRowSelection}
        >
          {actionItems}
        </div>
      )}
    </div>
  );
};

export default UserIdentityCard;
